import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, ExternalLink } from "lucide-react";

import ariseTv from "../../../assets/videos/arisetv.mp4";
import marketOutreach from "../../../assets/videos/market-outreach.mp4";
import zee from "../../../assets/zee.jpg";
import techFit from "../../../assets/techFit.jpeg";

const LatestNews = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  const news = [
    {
      title: "Arise TV Feature",
      eyebrow: "Featured Story",
      source: "ARISE TV",
      type: "WATCH",
      video: ariseTv,
      description:
        "At our HatchIt event, aspiring technology enthusiasts showed what becomes possible when talent meets access, mentorship and opportunity.",
      date: "NOV 13, 2024",
      time: "3 MIN WATCH",
      accent: "#59b5cd",
      featured: true,
      link: "https://www.linkedin.com/company/zulfah-group/",
    },

    {
      title: "Market Outreach",
      eyebrow: "Community",
      source: "ZULFAH NEWS",
      type: "WATCH",
      video: marketOutreach,
      description:
        "From Sabo Market, Ikorodu — stories of resilience, ambition and people proving that innovation can begin anywhere.",
      date: "NOV 13, 2024",
      time: "3 MIN WATCH",
      accent: "#ecb32c",
      link:
        "https://www.linkedin.com/feed/update/urn:li:activity:7259493401237090305",
    },

    {
      title: "TVC News Feature",
      eyebrow: "Media",
      source: "TVC NEWS",
      type: "WATCH",
      video: "https://www.youtube.com/embed/OM72DSHXj5s",
      description:
        "TechFit brought together learners who discovered that technology isn't reserved for a select few — it belongs to anyone willing to build.",
      date: "NOV 13, 2024",
      time: "3 MIN WATCH",
      accent: "#cf375d",
      link: "https://www.youtube.com/embed/OM72DSHXj5s",
    },

    {
      title: "Why People in Tech Are Rising Fast",
      eyebrow: "Insight",
      source: "VANGUARD",
      type: "READ",
      img: zee,
      description:
        "Zulfah Academy explores the skills, resilience and opportunities helping Nigeria's technology professionals accelerate their careers.",
      date: "NOV 10, 2024",
      time: "4 MIN READ",
      accent: "#8b5cf6",
      link:
        "https://www.vanguardngr.com/2024/11/why-people-in-tech-are-rich-zulfah/",
    },

    {
      title: "Build Here. Grow Here.",
      eyebrow: "Perspective",
      source: "PUNCH",
      type: "READ",
      img: techFit,
      description:
        "Why Nigeria's growing technology ecosystem presents opportunities worth building on — before looking elsewhere.",
      date: "NOV 08, 2024",
      time: "5 MIN READ",
      accent: "#10b981",
      link:
        "https://punchng.com/firm-canvasses-tech-skills-development/#google_vignette",
    },
  ];

  const featured = news[0];
  const secondary = news.slice(1);

  const openVideo = (index) => {
    setActiveVideo(index);
  };

  return (
    <section className="relative overflow-hidden bg-[#f7f8fa] py-24 sm:py-32">

      {/* =========================================================
          AMBIENT BACKGROUND
      ========================================================= */}

      <div className="absolute inset-0 pointer-events-none overflow-hidden">

        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/10 blur-[140px]" />

        <div className="absolute top-[35%] -left-40 w-[450px] h-[450px] rounded-full bg-[#ecb32c]/10 blur-[140px]" />

        <div className="absolute bottom-0 right-[20%] w-[350px] h-[350px] rounded-full bg-[#cf375d]/5 blur-[130px]" />

        {/* editorial grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(#07151c 1px, transparent 1px), linear-gradient(90deg, #07151c 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      <div className="relative max-w-[90rem] mx-auto px-5 sm:px-8 lg:px-12">

        {/* =========================================================
            HEADER
        ========================================================= */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16"
        >

          <div>

            <div className="flex items-center gap-3 mb-5">

              <span className="w-10 h-[2px] bg-[#ecb32c]" />

              <span className="text-xs font-bold tracking-[0.35em] uppercase text-[#cf375d]">
                Zulfah Journal
              </span>

            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#07151c] leading-[0.95]">
              In the
              <br />
              <span className="text-[#59b5cd]">News.</span>
            </h2>

            <p className="mt-6 max-w-xl text-gray-500 text-base sm:text-lg leading-relaxed">
              Stories, ideas and moments from Zulfah's journey —
              captured across media, communities and the technology
              ecosystem.
            </p>

          </div>

          {/* RIGHT SIDE INDEX */}

          <div className="hidden lg:flex items-center gap-4">

            <div className="h-px w-20 bg-gray-300" />

            <span className="text-xs font-bold tracking-[0.3em] text-gray-400 uppercase">
              Latest Stories
            </span>

          </div>

        </motion.div>


        {/* =========================================================
            FEATURED STORY
        ========================================================= */}

        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="group relative grid lg:grid-cols-[1.35fr_0.65fr] bg-[#07151c] rounded-[2rem] overflow-hidden min-h-[520px] shadow-[0_30px_80px_rgba(7,21,28,0.18)]"
        >

          {/* MEDIA */}

          <div className="relative min-h-[350px] lg:min-h-full overflow-hidden">

            <AnimatePresence mode="wait">

              {activeVideo === 0 ? (

                featured.video.includes("youtube") ? (

                  <motion.iframe
                    key="iframe"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={`${featured.video}?autoplay=1`}
                    title={featured.title}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />

                ) : (

                  <motion.video
                    key="video"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={featured.video}
                    controls
                    autoPlay
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                )

              ) : (

                <motion.div
                  key="thumbnail"
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => openVideo(0)}
                >

                  <video
                    src={featured.video}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1.2s]"
                  />

                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />

                  {/* PLAY */}

                  <div className="absolute inset-0 flex items-center justify-center">

                    <div className="relative">

                      <div className="absolute inset-0 rounded-full bg-[#ecb32c]/40 blur-xl animate-pulse" />

                      <div className="relative w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-500">

                        <Play
                          size={28}
                          fill="currentColor"
                          className="text-[#07151c] ml-1"
                        />

                      </div>

                    </div>

                  </div>

                  {/* FEATURED LABEL */}

                  <div className="absolute top-6 left-6">

                    <span className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-bold tracking-[0.2em] uppercase">
                      Featured Story
                    </span>

                  </div>

                </motion.div>

              )}

            </AnimatePresence>

          </div>


          {/* CONTENT */}

          <div className="relative flex flex-col justify-between p-8 sm:p-10 lg:p-12 text-white">

            <div>

              <div className="flex items-center justify-between mb-10">

                <span className="text-[#ecb32c] text-xs font-bold tracking-[0.3em] uppercase">
                  {featured.source}
                </span>

                <span className="text-white/30 text-5xl font-black">
                  01
                </span>

              </div>

              <p className="text-white/40 text-xs font-semibold tracking-[0.2em] uppercase">
                {featured.eyebrow}
              </p>

              <h3 className="mt-4 text-3xl sm:text-4xl font-black leading-tight">
                {featured.title}
              </h3>

              <p className="mt-6 text-white/60 leading-relaxed text-sm sm:text-base">
                {featured.description}
              </p>

            </div>

            <div className="mt-10">

              <div className="flex items-center gap-3 text-xs text-white/40 uppercase tracking-wider mb-6">

                <span>{featured.date}</span>

                <span className="w-1 h-1 rounded-full bg-white/30" />

                <span>{featured.time}</span>

              </div>

              <a
                href={featured.link}
                target="_blank"
                rel="noreferrer"
                className="group/link inline-flex items-center gap-3 text-white font-semibold"
              >

                <span className="relative">
                  Explore Story

                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#ecb32c] group-hover/link:w-full transition-all duration-500" />
                </span>

                <span className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center group-hover/link:bg-[#ecb32c] group-hover/link:text-[#07151c] group-hover/link:border-[#ecb32c] transition-all">

                  <ArrowUpRight size={16} />

                </span>

              </a>

            </div>

          </div>

        </motion.article>


        {/* =========================================================
            STORY GRID
        ========================================================= */}

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">

          {secondary.map((item, index) => {

            const realIndex = index + 1;

            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                }}
                className="group relative bg-white rounded-[1.5rem] overflow-hidden border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-500"
              >

                {/* MEDIA */}

                <div className="relative h-[230px] overflow-hidden bg-[#07151c]">

                  {item.video ? (

                    activeVideo === realIndex ? (

                      item.video.includes("youtube") ? (

                        <iframe
                          src={`${item.video}?autoplay=1`}
                          title={item.title}
                          className="w-full h-full"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />

                      ) : (

                        <video
                          src={item.video}
                          controls
                          autoPlay
                          className="w-full h-full object-cover"
                        />

                      )

                    ) : (

                      <div
                        onClick={() => openVideo(realIndex)}
                        className="relative w-full h-full cursor-pointer"
                      >

                        <video
                          src={item.video}
                          muted
                          autoPlay
                          loop
                          playsInline
                          className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
                        />

                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all" />

                        <div className="absolute inset-0 flex items-center justify-center">

                          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">

                            <Play
                              size={20}
                              fill="currentColor"
                              className="text-[#07151c] ml-1"
                            />

                          </div>

                        </div>

                      </div>

                    )

                  ) : (

                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />

                  )}

                  {/* INDEX */}

                  <div className="absolute top-4 right-4">

                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md text-white text-xs font-bold">
                      {String(realIndex + 1).padStart(2, "0")}
                    </span>

                  </div>

                </div>


                {/* CONTENT */}

                <div className="p-6">

                  <div className="flex items-center justify-between mb-4">

                    <span
                      className="text-[10px] font-bold tracking-[0.2em] uppercase"
                      style={{ color: item.accent }}
                    >
                      {item.source}
                    </span>

                    <span className="text-[10px] font-semibold tracking-wider text-gray-400">
                      {item.type}
                    </span>

                  </div>

                  <h3 className="text-xl font-black text-[#07151c] leading-tight group-hover:text-[#59b5cd] transition-colors">

                    {item.title}

                  </h3>

                  <p className="text-sm text-gray-500 leading-relaxed mt-4">

                    {item.description}

                  </p>

                  <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">

                    <span className="text-[10px] text-gray-400 font-semibold tracking-wider">
                      {item.date}
                    </span>

                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-[#07151c] hover:text-white transition-all"
                    >

                      {item.type === "WATCH" ? (
                        <Play size={14} />
                      ) : (
                        <ExternalLink size={14} />
                      )}

                    </a>

                  </div>

                </div>

              </motion.article>
            );
          })}

        </div>


        {/* =========================================================
            EDITORIAL FOOTER
        ========================================================= */}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-20 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
        >

          <div>

            <p className="text-xs font-bold tracking-[0.25em] uppercase text-gray-400">
              Beyond the classroom
            </p>

            <p className="mt-2 text-gray-500 text-sm">
              Follow the ideas, people and stories shaping Zulfah.
            </p>

          </div>

          <a
            href="https://www.linkedin.com/company/zulfah-group/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#07151c] hover:text-[#59b5cd] transition-colors"
          >

            Follow Zulfah

            <ArrowUpRight size={16} />

          </a>

        </motion.div>

      </div>

    </section>
  );
};

export default LatestNews;